import classNames from '../../layout/classNames';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import { useMutation } from '@apollo/client';
import {
    PrimaryForecastingMutation,
    PrimaryForecastingMutationVariables
} from '../../data/__generated__/PrimaryForecastingMutation';
import { CANCEL_FORECASTING_MUTATION, PRIMARY_FORECASTING_MUTATION } from '../../data/ForecastingResults';
import {
    CancelForecastingMutation,
    CancelForecastingMutationVariables
} from '../../data/__generated__/CancelForecastingMutation';
import { ReactComponent as LoadingState } from 'svg/loader.svg';
import { ForecastingState, useForecastingSettings, useForecastingState } from '../ForecastingContextProvider';
import { DateRangeStatus, usePrimaryForecastingState } from './ForecastingPanel';
import ForecastingCard from '../ForecastingCard';

export default function ForecastingRunner() {
    const { forecastingSettings } = useForecastingSettings();
    const { forecastingState, forecastingId, updateForecastingId } = useForecastingState();
    const { isPrimaryForecastingLoading, setPrimaryForecastingLoading, inputDateRangeStatus } = usePrimaryForecastingState();

    const [fetchNewForecastingId, { loading: primaryForecastingInitiallyLoading }] =
        useMutation<PrimaryForecastingMutation, PrimaryForecastingMutationVariables>(PRIMARY_FORECASTING_MUTATION);
    useEffect(() => {
        setPrimaryForecastingLoading(primaryForecastingInitiallyLoading || forecastingState === 'transient' || forecastingState === 'unknown');
    }, [primaryForecastingInitiallyLoading, forecastingState]);

    const [cancelForecastingCall, { loading: cancelForecastingPending }] =
        useMutation<CancelForecastingMutation, CancelForecastingMutationVariables>(CANCEL_FORECASTING_MUTATION, {
            onCompleted: (data) => {
                updateForecastingId(null);
            }
        });
    const cancelForecasting = useCallback(() => cancelForecastingCall({
        variables: {
            forecastingId: forecastingId!
        }
    }), [forecastingId]);

    const doPrimaryForecasting = async () => {
        if (Notification && Notification.permission === 'default') {
            Notification.requestPermission().then(_ => {
            });
        }
        updateForecastingId(null);
        await fetchNewForecastingId({
            variables: {
                ssp: forecastingSettings.ssp,
                campaignStart: forecastingSettings.dateRange[0],
                campaignEnd: forecastingSettings.dateRange[1],
                hourOfWeeks: forecastingSettings.hourOfWeekFilter
                    .flatMap((hours, day) => hours
                        .map((include, hour) => ({ day, hour, include }))
                    )
                    .filter(it => it.include)
                    .map(it => it.day * 24 + it.hour),
                publisher: forecastingSettings.publisherFilter,
                country: forecastingSettings.countryFilter,
                region: forecastingSettings.regionFilter,
                metro: forecastingSettings.metroFilter?.map(it => it.value),
                city: forecastingSettings.cityFilter,
                zip: forecastingSettings.zipFilter,
                creativeTypes: forecastingSettings.creativeTypes,
                venueTypes: forecastingSettings.venueTypeFilter?.map(it => it.value),
                audienceId: forecastingSettings.enableAudienceTargeting ? forecastingSettings.audienceId : null,
                includeAudienceTier1: forecastingSettings.enableAudienceTargeting ? forecastingSettings.includeAudienceTier1 : null,
                includeAudienceTier2: forecastingSettings.enableAudienceTargeting ? forecastingSettings.includeAudienceTier2 : null,
                includeAudienceTier3: forecastingSettings.enableAudienceTargeting ? forecastingSettings.includeAudienceTier3 : null,
                includeAudienceTier4: forecastingSettings.enableAudienceTargeting ? forecastingSettings.includeAudienceTier4 : null,
                winRate: forecastingSettings.winRate,
                pointsOfInterest: forecastingSettings.poiList,
                polygonsOfInterest: forecastingSettings.polygonList,
                poiType: forecastingSettings.poiType
            },
            onCompleted: (data) => updateForecastingId(data.primaryForecasting.forecastingId)
        });
    };

    const startedLoadingTimestamp = useRef<number | null>(null);
    const [showAlert, setShowAlert] = useState(false);
    useEffect(() => {
        const stateToNotifyMessage: ReadonlyMap<ForecastingState, string> = new Map([
            ['failed', 'failed'],
            ['loaded', 'complete']
        ]);
        const notifyStates: ReadonlySet<ForecastingState> = new Set(stateToNotifyMessage.keys());

        const notify = (forecastingStatus: ForecastingState) => {
            if (!Notification || Notification.permission !== 'granted') return;
            new Notification(`Forecast ${stateToNotifyMessage.get(forecastingStatus)}`);
        };

        if (startedLoadingTimestamp.current === null) {
            if (forecastingState === 'transient') startedLoadingTimestamp.current = Date.now();
        } else if (notifyStates.has(forecastingState)) {
            // if it is annoying having notifications for fast forecasts in the future we can check the timestamp here
            notify(forecastingState);
            startedLoadingTimestamp.current = null;
        }

        if (forecastingState == 'failed') {
            setShowAlert(true);
        } else {
           setShowAlert(false);
        }
    }, [forecastingState]);
    
    const isDisabled = forecastingSettings.ssp.length == 0 || inputDateRangeStatus != DateRangeStatus.Valid;
    return (
        <div className='mt-4 w-80'>
            <div className='w-fit' hidden={inputDateRangeStatus == DateRangeStatus.Valid}>
                <span className='block text-s font-bold text-red-700 italic'>
                {
                    inputDateRangeStatus == DateRangeStatus.PastDate
                    ? 'The forecast date range cannot include dates in the past. Please update your date range to generate a new forecast.'
                    : 'The forecast date range is invalid. It must be "start - end" dates using MM/DD/YYYY date format.'
                }
                </span>
            </div>
            <div className='w-fit' hidden={forecastingSettings.ssp.length != 0}>
                <span
                    className='block text-s font-bold text-red-700 italic'
                >
                    Please select SSP.
                </span>
            </div>
            <div className='w-80 h-10 mt-4 flex'>
                <button
                    className={classNames(
                        'flex-auto bg-blue-700 text-white font-bold py-2 px-4 rounded',
                        isDisabled ? 'cursor-not-allowed opacity-50v bg-blue-200' : 'hover:bg-blue-800'
                    ) }
                    onClick={(_) => doPrimaryForecasting()}
                    disabled={isDisabled}
                    hidden={isPrimaryForecastingLoading}
                >
                    Run forecasting
                </button>
                <button
                    className={classNames(
                        'flex-auto bg-red-500 text-white font-bold py-2 px-4 rounded',
                        cancelForecastingPending || forecastingId === null || forecastingState === 'unknown'
                            ? 'cursor-not-allowed opacity-50'
                            : 'hover:bg-red-700'
                    )}
                    onClick={(_) => cancelForecasting()}
                    hidden={!isPrimaryForecastingLoading}
                    disabled={cancelForecastingPending || forecastingId === null || forecastingState === 'unknown'}
                >
                    Cancel
                </button>
                <div style={{backgroundColor: 'transparent', position: 'fixed', top: '50%', left:'50%', transform: 'translate(-50%, -50%)', maxWidth: '400px', zIndex:9999, }}>
                    {showAlert ?
                        <ForecastingCard
                            footer={
                                <button
                                    className={classNames(
                                        'flex-auto bg-red-500 text-white font-bold py-2 px-4 rounded',
                                        'hover:bg-red-700'
                                    )}
                                    onClick={()=>{setShowAlert(false)}}
                                >
                                    OK
                                </button>}>
                            <div className="w-fit">
                                <span style={{display: 'block', fontWeight: '600', fontSize: '16px', color: 'text-gray-600'}}>
                                    Error
                                </span>
                            </div>
                            <div>
                                There was an error while running this forecast.
                                Contact the team for further investigation and provide the link in your browser.
                            </div>
                        </ForecastingCard>
                        : null}
                </div>
                {isPrimaryForecastingLoading && (
                    <LoadingState className='flex-auto CampaignSpendOverlayLoadingState h-10 w-10' />
                )}
            </div>
        </div>
    );
}